import React, { useEffect, useState, useCallback } from 'react';
import ProjectorPOVWindow from './ProjectorPOVWindow';
import { useApp } from '../../context/AppContext';

/**
 * Container that manages all open Projector POV floating windows.
 * Listens for `projector-pov-open` and `projector-pov-close` events dispatched
 * from the Scene panel, and stacks new windows with an offset cascade.
 */
const ProjectorPOVWindowsLayer = () => {
  const { projectors } = useApp();
  const [openIds, setOpenIds] = useState([]); // [{id, x, y}]

  const open = useCallback((id) => {
    if (typeof window.openProjectorPOVWindow !== 'function') return;
    setOpenIds(prev => {
      if (prev.find(w => w.id === id)) {
        window.closeProjectorPOVWindow?.(id);
        return prev.filter(w => w.id !== id);
      }
      // Initial RTT size — width-driven, height locked to projector aspect.
      // Default 16:10 (480 × 300) if projector hasn't picked an aspect yet.
      const proj = projectors.find(p => p.id === id);
      const aspectStr = proj?.aspect_ratio || '16:10';
      let aspect = 16 / 10;
      if (aspectStr.includes(':')) {
        const [a, b] = aspectStr.split(':').map(Number);
        if (a > 0 && b > 0) aspect = a / b;
      }
      const initW = 480;
      const initH = Math.round(initW / aspect);
      const result = window.openProjectorPOVWindow(id, { width: initW, height: initH });
      if (!result) return prev;
      const offset = prev.length * 28;
      return [...prev, { id, x: 80 + offset, y: 100 + offset }];
    });
  }, [projectors]);

  const close = useCallback((id) => {
    setOpenIds(prev => prev.filter(w => w.id !== id));
  }, []);

  useEffect(() => {
    const onOpen = (e) => { if (e?.detail?.id) open(e.detail.id); };
    window.addEventListener('projector-pov-open', onOpen);
    return () => window.removeEventListener('projector-pov-open', onOpen);
  }, [open]);

  // If a projector is removed from the scene while its POV window is open,
  // tear it down to avoid stale state.
  useEffect(() => {
    setOpenIds(prev => {
      const valid = prev.filter(w => projectors.some(p => p.id === w.id));
      if (valid.length !== prev.length) {
        prev.forEach(w => {
          if (!projectors.some(p => p.id === w.id)) {
            window.closeProjectorPOVWindow?.(w.id);
          }
        });
      }
      return valid;
    });
  }, [projectors]);

  return (
    <>
      {openIds.map(({ id, x, y }) => {
        const proj = projectors.find(p => p.id === id);
        return (
          <ProjectorPOVWindow
            key={id}
            projectorId={id}
            projectorName={proj?.name || id}
            projectorAspect={proj?.aspect_ratio || '16:10'}
            initialX={x}
            initialY={y}
            initialW={480}
            onClose={close}
          />
        );
      })}
    </>
  );
};

export default ProjectorPOVWindowsLayer;
